import React, { useMemo } from 'react';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import SquareIcon from '@mui/icons-material/Square';
import makeStyles from '@mui/styles/makeStyles';
import { useIntl } from 'react-intl';
import {
  VictoryBar,
  VictoryContainer,
  VictoryStack,
  VictoryTooltip
} from 'victory';
import {
  getFormattedDuration,
  useFormattedDuration
} from 'modules/notes/common/components/Timer';
import { categoriesDataType, roundedDuration } from './utils';
import { useChartStyles } from './hooks/useChartStyles';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(0, 1),
    width: '100%',
    justifyContent: 'center',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: 'fit-content'
  },
  overTimeIcon: {
    top: 0,
    right: 0,
    position: 'absolute',
    width: theme.spacing(1),
    height: theme.spacing(1),
    border: '4px solid transparent',
    borderTopColor: 'rgba(133, 109, 219, 1)',
    borderRightColor: 'rgba(133, 109, 219, 1)'
  },
  dataContainer: {
    width: '147% !important',
    right: '23.5%',
    height: '50% !important',
    display: ({ hasCategoryData }: { hasCategoryData: boolean }) =>
      !hasCategoryData ? 'none' : 'flex'
  }
}));
const useFlyoutStyles = makeStyles((theme) => ({
  toolTipBox: {
    display: 'flex',
    alignItems: 'center'
  },
  toolSquareIcon: {
    width: theme.spacing(1.65),
    height: theme.spacing(1.65),
    marginRight: theme.spacing(0.4)
  }
}));

type UtilizationHoursCellProps = {
  actualDuration: number;
  scheduledDuration: number;
  categoryData: categoriesDataType[];
};

const MyCustomFlyout: React.FC<any> = ({
  y,
  categoryData
}: {
  y: number;
  categoryData: categoriesDataType[];
}) => {
  const { formatMessage } = useIntl();
  const classes = useFlyoutStyles();
  const theme = useTheme();
  const toolTipElement = useMemo(() => {
    return categoryData.map((i) => {
      const formattedTime = getFormattedDuration({
        formatMessage,
        duration: i.data[0].totalDuration ?? 0,
        needsMask: true
      });
      return (
        <div key={i.categoryName} className={classes.toolTipBox}>
          <SquareIcon
            sx={{ color: i.color }}
            className={classes.toolSquareIcon}
          />
          <Typography variant="caption">
            {`${i.categoryName}: ${formattedTime}`}
          </Typography>
          <br />
        </div>
      );
    });
  }, [categoryData, classes.toolSquareIcon, classes.toolTipBox, formatMessage]);

  return (
    <foreignObject
      x={200}
      y={y + 10}
      width={theme.spacing(8.25)}
      height={theme.spacing(8.25)}
    >
      <Tooltip title={toolTipElement} open={true} arrow>
        <span />
      </Tooltip>
    </foreignObject>
  );
};

const UtilizationHoursCell: React.FC<UtilizationHoursCellProps> = ({
  actualDuration,
  scheduledDuration,
  categoryData
}: UtilizationHoursCellProps) => {
  const classes = useStyles({ hasCategoryData: categoryData.length > 0 });

  const formattedDuration = useFormattedDuration({
    needsMask: true,
    duration: actualDuration ?? 0
  });

  const theme = useTheme();
  const style = useChartStyles(theme);

  return (
    <Box
      className={classes.container}
      data-testid={'work-hours-cell-container'}
    >
      {roundedDuration(actualDuration) > roundedDuration(scheduledDuration) && (
        <div
          className={classes.overTimeIcon}
          data-testid={'work-hours-cell-overtime'}
        />
      )}
      <div>
        <Typography variant="caption" alignSelf="center">
          {formattedDuration}
        </Typography>
      </div>
      <VictoryStack
        horizontal
        height={style.VictoryStack.height}
        containerComponent={
          <VictoryContainer className={classes.dataContainer} />
        }
      >
        {categoryData.length > 0 &&
          categoryData.map((x) => {
            return (
              <VictoryBar
                data-testid={`bar-${x.categoryName}`}
                key={x.categoryName}
                barWidth={style.VictoryBar.barWidth}
                data={x.data}
                style={{
                  data: {
                    fill: x.color
                  }
                }}
                x="date"
                y="totalDuration"
                labels={() => ''}
                labelComponent={
                  <VictoryTooltip
                    flyoutPadding={style.VictoryTooltip.padding}
                    flyoutComponent={
                      <MyCustomFlyout categoryData={categoryData} />
                    }
                  />
                }
                events={[
                  {
                    target: 'data',
                    eventHandlers: {
                      onMouseOver: () => {
                        return [
                          {
                            target: 'data',
                            mutation: () => {
                              return {
                                style: {
                                  fill: x.color,
                                  stroke: theme.palette.information.background,
                                  strokeWidth: 1,
                                  cursor: 'pointer'
                                }
                              };
                            }
                          },
                          {
                            target: 'labels',
                            mutation: () => ({ active: true })
                          }
                        ];
                      },
                      onMouseLeave: () => {
                        return [
                          {
                            target: 'data',
                            mutation: () => {
                              return {
                                style: {
                                  fill: x.color,
                                  strokeWidth: 0
                                }
                              };
                            }
                          },
                          {
                            target: 'labels',
                            mutation: () => ({ active: undefined })
                          }
                        ];
                      }
                    }
                  }
                ]}
              />
            );
          })}
      </VictoryStack>
    </Box>
  );
};
export default React.memo(UtilizationHoursCell);
